import {
  Action,
  Actions,
  Alert,
  AlertProps,
  Breadcrumbs,
  BreadcrumbType,
  Separator,
  TabsHeader,
} from 'platform/components';
import {Box, Heading, HStack, Show, Space, Text, VStack} from 'platform/foundation';

import {ReactNode} from 'react';
import {Fragment} from 'react/jsx-runtime';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {queryParams, SectionTab} from '@dms/shared';

import {suffixTestId, TestIdProps, useQueryState} from 'shared/web';

export interface SettingsHeaderProps extends TestIdProps {
  title?: string;
  titleAddon?: ReactNode;
  subtitle?: string;
  isTitleTeriary?: boolean;
  breadcrumbs?: BreadcrumbType[];
  actions?: Action[];
  tabs?: SectionTab[];
  onTabChange?: (tabId: string) => void;
  alerts?: AlertProps[];
}

export function SettingsHeaderV2(props: SettingsHeaderProps) {
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const tabs = (props.tabs ?? [])?.map(({queryId, ...item}) => ({
    id: queryId,
    ...item,
    'data-testid': suffixTestId(queryId, props),
  }));

  return (
    <VStack>
      <Box padding={6}>
        <Show when={isNotNilOrEmpty(props.breadcrumbs)}>
          <Breadcrumbs
            breadcrumbs={props.breadcrumbs}
            data-testid={suffixTestId('settingsHeader-breadcrumbs', props)}
          />
          <Space vertical={8} />
        </Show>
        <HStack align="center" spacing={3} justify="space-between">
          <HStack
            align="center"
            spacing={3}
            data-testid={suffixTestId('settingsHeader-titleRow', props)}
          >
            <Heading
              size={1}
              color={props.isTitleTeriary ? 'tertiary' : undefined}
              data-testid={suffixTestId('settingsHeader-title', props)}
            >
              {props.title}
            </Heading>
            {props.titleAddon}
          </HStack>
          <Actions
            actions={props.actions}
            data-testid={suffixTestId('settingsHeader-actions', props)}
          />
        </HStack>
        <Show when={isNotNil(props.subtitle)}>
          <Space vertical={4} />
          <Text size="small" color="secondary">
            {props.subtitle}
          </Text>
        </Show>
      </Box>

      <Show when={isNotNil(props.tabs)}>
        <Separator spacing={0} />

        <Box paddingHorizontal={6}>
          <TabsHeader
            data-testid={suffixTestId('settingsHeader-tabs', props)}
            tabs={tabs}
            activeTabId={activeTabId}
            onChange={props.onTabChange ?? setActiveTabId}
          />
        </Box>
      </Show>

      <Show when={props.alerts}>
        {props.alerts?.map((alert, index) => (
          // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
          <Fragment key={index}>
            <Alert {...alert} data-testid={suffixTestId(`entityHeader-alert-[${index}]`, props)} />
            <Show when={index < (props.alerts?.length ?? 0) - 1}>
              <Space vertical={1} />
            </Show>
          </Fragment>
        ))}
      </Show>

      <Separator spacing={0} />
    </VStack>
  );
}
